import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect } from 'react';
import type { AxiosError } from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppLayout from '../components/AppLayout';
import { ObjectiveMeasureRatingCard } from '../components/ObjectiveMeasureRatingCard';
import { BehaviouralMeasureRatingCard } from '../components/BehaviouralMeasureRatingCard';
import { API_ENDPOINTS } from '../constants/api';
import { normaliseRating } from '../utils/reviewRatingUtils';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { genericCopy } from '../utils/userFacingCopy';

// ── Types ──────────────────────────────────────────────────────────────────

interface PeerObjectiveToRate {
  objectiveMeasureId: string;
  objectiveMeasureName: string;
  level1Criteria: string;
  level2Criteria: string;
  level3Criteria: string;
}

interface PeerBehaviouralMeasure {
  behaviouralMeasureId: string;
  behaviouralMeasureName: string;
}

interface ExistingObjectiveRating {
  objectiveMeasureId: string;
  rating?: number | string;
  comment?: string;
}

interface ExistingBehaviouralRating {
  behaviouralMeasureId: string;
  rating?: number | string;
  comment?: string;
}

interface PeerReviewView {
  reviewId: string;
  reviewCycleName: string;
  employeeName: string;
  employeeCutoff?: string;
  hasSubmittedAllObjectives: boolean;
  objectivesToRate: PeerObjectiveToRate[];
  existingObjectiveRatings: ExistingObjectiveRating[];
  behaviouralMeasures: PeerBehaviouralMeasure[];
  existingBehaviouralRatings: ExistingBehaviouralRating[];
  peerRatingNoLongerAccepted?: boolean;
  peerRatingNoLongerAcceptedReason?: string | null;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ReviewPeerReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [peerView, setPeerView] = useState<PeerReviewView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  // Objective rating form state
  const [objectiveRatings, setObjectiveRatings] = useState<
    { objectiveMeasureId: string; rating: number; comment: string }[]
  >([]);
  const [objectiveErrors, setObjectiveErrors] = useState<number[]>([]);

  // Behavioural rating form state (N/A = rating 0 + comment, same as manager/employee)
  const [behaviouralRatings, setBehaviouralRatings] = useState<
    { behaviouralMeasureId: string; rating: number; comment: string }[]
  >([]);
  const [behaviouralError, setBehaviouralError] = useState('');

  // ── Load ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) {
      setError('Invalid review ID');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await apiService.get<PeerReviewView>(API_ENDPOINTS.REVIEWS.PEER_VIEW(id));
        if (cancelled) return;
        const view = res.data;
        setPeerView(view);

        // Pre-fill objective ratings from any prior submissions
        setObjectiveRatings(
          view.objectivesToRate.map((o) => {
            const existing = view.existingObjectiveRatings.find(
              (e) => e.objectiveMeasureId === o.objectiveMeasureId
            );
            return {
              objectiveMeasureId: o.objectiveMeasureId,
              rating: normaliseRating(existing?.rating) ?? 0,
              comment: existing?.comment ?? '',
            };
          })
        );

        // Pre-fill behavioural ratings from any prior submissions (N/A = rating 0 + comment)
        setBehaviouralRatings(
          view.behaviouralMeasures.map((b) => {
            const existing = view.existingBehaviouralRatings.find(
              (e) => e.behaviouralMeasureId === b.behaviouralMeasureId
            );
            const ratingVal = existing?.rating;
            const numRating = typeof ratingVal === 'number' ? (ratingVal === 0 ? 0 : normaliseRating(ratingVal)) : normaliseRating(ratingVal);
            return {
              behaviouralMeasureId: b.behaviouralMeasureId,
              rating: numRating ?? 0,
              comment: existing?.comment ?? '',
            };
          })
        );
      } catch (err) {
        if (cancelled) return;
        const axiosError = err as AxiosError<{ message?: string }>;
        if (axiosError.response?.status === 403) {
          const msg = getApiErrorMessage(err, 'You are not an approved peer reviewer for this review.');
          if (msg.toLowerCase().includes('deadline')) {
            setDeadlinePassed(true);
            setError(msg);
          } else {
            setError(msg);
          }
        }
        else if (axiosError.response?.status === 404)
          setError('Review not found.');
        else if (axiosError.response?.status === 401)
          navigate('/login');
        else {
          setError(getApiErrorMessage(err, genericCopy.peerReview.load));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!peerView || !id) return;
    if (peerView.peerRatingNoLongerAccepted) return;

    // Validate: all objectives must have a rating (1–3) or N/A (0) with a reason
    const unrated = objectiveRatings
      .map((r, i) => ({ i, ...r }))
      .filter(({ rating }) => rating === undefined || rating === null || (typeof rating === 'number' && rating < 0))
      .map(({ i }) => i);
    if (unrated.length > 0) {
      setObjectiveErrors(unrated);
      return;
    }
    const naWithoutReason = objectiveRatings
      .map((r, i) => ({ i, ...r }))
      .filter(({ rating, comment }) => rating === 0 && !comment?.trim())
      .map(({ i }) => i);
    if (naWithoutReason.length > 0) {
      setObjectiveErrors(naWithoutReason);
      return;
    }
    setObjectiveErrors([]);

    // Validate: behavioural measures must have rating (1–3 or 0 for N/A) and comment (required for all)
    for (const br of behaviouralRatings) {
      const name = peerView.behaviouralMeasures.find(
        (b) => b.behaviouralMeasureId === br.behaviouralMeasureId
      )?.behaviouralMeasureName ?? 'a measure';
      if (typeof br.rating !== 'number' || br.rating < 0 || br.rating > 3) {
        setBehaviouralError(`Please select a rating for "${name}"`);
        return;
      }
      if (!br.comment.trim()) {
        setBehaviouralError(br.rating === 0 ? `Please provide a reason for marking "${name}" as N/A` : `Please provide a comment for "${name}"`);
        return;
      }
    }
    setBehaviouralError('');

    try {
      setSubmitting(true);
      setSubmitError('');

      // POST all objective ratings in one batch
      await apiService.post(`/reviews/${id}/peer-objective-ratings`, {
        ratings: objectiveRatings.map((r) => ({
          objectiveMeasureId: r.objectiveMeasureId,
          rating: r.rating,
          comment: r.comment || undefined,
        })),
      });

      // POST behavioural ratings one at a time (N/A = rating 0 + comment, same as manager/employee)
      for (const br of behaviouralRatings) {
        await apiService.post(`/reviews/${id}/peer-ratings`, {
          behaviouralMeasureId: br.behaviouralMeasureId,
          rating: br.rating,
          comment: br.comment,
        });
      }

      setSuccess(true);
    } catch (err) {
            setSubmitError(getApiErrorMessage(err, genericCopy.peerReview.submit));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render: loading / error ───────────────────────────────────────────────

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (deadlinePassed) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, pb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <PeopleIcon sx={{ fontSize: 32, color: 'warning.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Peer feedback window closed
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Done
          </Button>
        </Box>
      </AppLayout>
    );
  }

  if (error || !peerView) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, pb: 4 }}>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'Review not found'}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2, textTransform: 'none' }}>
            Back to dashboard
          </Button>
        </Box>
      </AppLayout>
    );
  }

  // ── Render: success ───────────────────────────────────────────────────────

  if (success) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, pb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate('/')} sx={{ ml: -1 }} title="Back to dashboard" aria-label="Back to dashboard">
              <ArrowBackIcon />
            </IconButton>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Peer feedback: {peerView.employeeName}
            </Typography>
          </Box>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            Your peer feedback has been submitted successfully. Thank you.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Done
          </Button>
        </Box>
      </AppLayout>
    );
  }

  // ── Render: already submitted (objectives done, no behavioural) ────────────

  if (peerView.hasSubmittedAllObjectives && peerView.behaviouralMeasures.length === 0) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, pb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate('/')} sx={{ ml: -1 }} title="Back to dashboard" aria-label="Back to dashboard">
              <ArrowBackIcon />
            </IconButton>
            <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Peer feedback: {peerView.employeeName}
            </Typography>
          </Box>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            You have already submitted your peer ratings for this review. Thank you.
          </Alert>
          <Button variant="outlined" onClick={() => navigate('/')}>
            Back to dashboard
          </Button>
        </Box>
      </AppLayout>
    );
  }

  // ── Render: form ──────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, pb: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/')} sx={{ ml: -1 }} title="Back to dashboard" aria-label="Back to dashboard">
            <ArrowBackIcon />
          </IconButton>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Peer feedback: {peerView.employeeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {peerView.reviewCycleName}
            </Typography>
          </Box>
          {peerView.employeeCutoff && (
            <Chip
              label={`Submit by: ${formatDate(peerView.employeeCutoff)} (cycle end)`}
              color="default"
              variant="outlined"
              size="small"
            />
          )}
          {!peerView.peerRatingNoLongerAccepted && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You can submit peer feedback until the cycle end date above or until the manager finalises the review, whichever is first.
            </Typography>
          )}
        </Box>

        {/* Peer can no longer submit (review finalised, submitted for discussion, or deadline passed) */}
        {peerView.peerRatingNoLongerAccepted && peerView.peerRatingNoLongerAcceptedReason && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {peerView.peerRatingNoLongerAcceptedReason}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              There is nothing more to do on this page. Use the button below to return to your dashboard.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none' }}
            >
              Return to dashboard
            </Button>
          </>
        )}

        {/* Objectives already submitted — inform and allow behavioural update */}
        {peerView.hasSubmittedAllObjectives && !peerView.peerRatingNoLongerAccepted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your objective ratings have already been submitted. You can still update your behavioural feedback below.
          </Alert>
        )}

        {/* Submit error */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        )}

        {!peerView.peerRatingNoLongerAccepted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Rate the objectives and behavioural measures below for <strong>{peerView.employeeName}</strong>.
            Your ratings are anonymous to the employee. Select a rating for each item and add an optional comment.
          </Alert>
        )}

        {/* ── Objective measures (only when peer can still submit) ───────────── */}
        {!peerView.peerRatingNoLongerAccepted && peerView.objectivesToRate.length > 0 && (
          <>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Objective measures
            </Typography>

            {objectiveRatings.map((rating, index) => {
              const measure = peerView.objectivesToRate[index];
              const hasError = objectiveErrors.includes(index);
              return (
                <ObjectiveMeasureRatingCard
                  key={measure.objectiveMeasureId}
                  measureName={measure.objectiveMeasureName}
                  level1Criteria={measure.level1Criteria ?? '—'}
                  level2Criteria={measure.level2Criteria ?? '—'}
                  level3Criteria={measure.level3Criteria ?? '—'}
                  value={typeof rating.rating === 'number' ? rating.rating : null}
                  onRatingChange={(val) => {
                    setObjectiveErrors((prev) => prev.filter((i) => i !== index));
                    setObjectiveRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, rating: val ?? 0 } : r))
                    );
                  }}
                  comment={rating.comment}
                  onCommentChange={(val) => {
                    if (rating.rating === 0) setObjectiveErrors((prev) => prev.filter((i) => i !== index));
                    setObjectiveRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, comment: val } : r))
                    );
                  }}
                  error={hasError}
                  onClearError={() => setObjectiveErrors((prev) => prev.filter((i) => i !== index))}
                />
              );
            })}
          </>
        )}

        {/* ── Behavioural measures (only when peer can still submit) ──────── */}
        {!peerView.peerRatingNoLongerAccepted && peerView.behaviouralMeasures.length > 0 && (
          <>
            {peerView.objectivesToRate.length > 0 && <Divider sx={{ my: 3 }} />}
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Behavioural measures
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Rate each behavioural competency. If a measure is not applicable, select N/A and provide a reason in the comment.
            </Typography>

            {behaviouralError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBehaviouralError('')}>
                {behaviouralError}
              </Alert>
            )}

            {behaviouralRatings.map((brRating, index) => {
              const measure = peerView.behaviouralMeasures[index];
              return (
                <BehaviouralMeasureRatingCard
                  key={measure.behaviouralMeasureId}
                  measureName={measure.behaviouralMeasureName}
                  value={typeof brRating.rating === 'number' ? brRating.rating : 0}
                  onRatingChange={(val) => {
                    setBehaviouralError('');
                    setBehaviouralRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, rating: val } : r))
                    );
                  }}
                  comment={brRating.comment}
                  onCommentChange={(val) => {
                    setBehaviouralError('');
                    setBehaviouralRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, comment: val } : r))
                    );
                  }}
                  ratingLabel="Rating *"
                  commentRequired
                  onClearError={() => setBehaviouralError('')}
                />
              );
            })}
          </>
        )}

        {/* ── Submit button (only when peer can still submit) ─────────────── */}
        {!peerView.peerRatingNoLongerAccepted && (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : undefined}
            >
              {submitting ? 'Submitting…' : 'Submit peer feedback'}
            </Button>
            <Button
              variant="text"
              size="large"
              onClick={() => navigate('/')}
              disabled={submitting}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    </AppLayout>
  );
}
